// src/admin/WebExploreCardPreview.js
import React, { useState, useEffect } from 'react';
import { useViewedCards } from '../hooks/useViewedCards';

// Font mapping - using actual font names that match the @font-face declarations in App.css
const webFontMapping = {
    'AirbnbCereal_W_Lt': 'AirbnbCereal_W_Lt, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    'AirbnbCereal_W_Md': 'AirbnbCereal_W_Md, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    'PassFont': 'PassFont, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    'MainFontLight': 'MainFontLight, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    'Caveat-Bold': 'Caveat-Bold, cursive',  // Using actual Caveat-Bold font
    'Barlow-Bold': 'Barlow-Bold, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',  // Using actual Barlow-Bold font
    'system': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
};

// Helper function to get font with fallbacks
const getWebFont = (fontName) => {
    if (!fontName || fontName === 'system') {
        return webFontMapping.system;
    }
    return webFontMapping[fontName] || webFontMapping.system;
};

// Helper to format YYYY-MM-DD to "Month Day"
const formatDateForPreview = (dateString) => {
    if (!dateString) return '';
    try {
        const [year, month, day] = dateString.split('-');
        if (!year || !month || !day) return dateString;
        const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
        return dateString;
    }
};

// Helper to get text size values
const getTextSize = (size, type) => {
    // If it's already a number, return it as a pixel value
    if (typeof size === 'number') {
        return `${size}px`;
    }
    
    // Responsive sizes optimized for Barlow font readability
    // Based on 350px card width
    const sizes = {
        title: { small: '20px', medium: '26px', large: '32px' },
        location: { small: '11px', medium: '12px', large: '14px' },
        date: { small: '12px', medium: '14px', large: '16px' },
        customText: { small: '12px', medium: '14px', large: '16px' },
        badge: { small: '11px', medium: '12px', large: '14px' },
        detailTitle: { small: '16px', medium: '20px', large: '24px' },
        detailText: { small: '14px', medium: '16px', large: '18px' },
    };
    return sizes[type] ? sizes[type][size] || sizes[type].medium : '14px';
};

// Helper to convert React Native style to Web CSS
// This makes the web preview behave more like React Native
const rnToWebStyle = (style) => {
    if (!style) return {};
    
    const webStyle = { ...style };
    
    // Convert paddingHorizontal/paddingVertical to web equivalents
    if (style.paddingHorizontal !== undefined) {
        webStyle.paddingLeft = style.paddingHorizontal;
        webStyle.paddingRight = style.paddingHorizontal;
        delete webStyle.paddingHorizontal;
    }
    
    if (style.paddingVertical !== undefined) {
        webStyle.paddingTop = style.paddingVertical;
        webStyle.paddingBottom = style.paddingVertical;
        delete webStyle.paddingVertical;
    }
    
    // React Native defaults for flex containers
    if (style.display === 'flex' || style.flex !== undefined) {
        // React Native flex defaults
        webStyle.display = 'flex';
        webStyle.flexDirection = style.flexDirection || 'column'; // RN default is column, not row
        webStyle.flexShrink = style.flexShrink !== undefined ? style.flexShrink : 1; // RN default
        
        if (style.flex === 1) {
            webStyle.flexGrow = 1;
            webStyle.flexShrink = 1;
            webStyle.flexBasis = '0%';
        }
    }
    
    // Handle alignSelf
    if (style.alignSelf === 'flex-start' || style.alignSelf === 'flex-end' || style.alignSelf === 'center') {
        webStyle.alignSelf = style.alignSelf;
    }
    
    // Convert number values to pixels (React Native uses numbers for pixels)
    ['width', 'height', 'fontSize', 'borderRadius', 'margin', 'marginTop', 'marginBottom', 'marginLeft', 'marginRight', 
     'padding', 'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight'].forEach(prop => {
        if (typeof webStyle[prop] === 'number') {
            webStyle[prop] = `${webStyle[prop]}px`;
        }
    });
    
    return webStyle;
};

function WebExploreCardPreview({
    title,
    locationTag,
    startDate,
    endDate,
    imageUrl,
    galleryImageUrls = [],
    showGallery = true,
    coverStyling = {},
    coverSections = [],
    coverBackgroundStyle = {},
    detailSections = [],
    detailImagePreviews = {},
    detailBackgroundColor = '#ffffff',
    showCoverOnly = false,
    showDetailOnly = false,
    previewPlatform = 'web',
    cardId = null,
}) {
    const [isFlipped, setIsFlipped] = useState(showDetailOnly);
    const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
    const { isViewed, trackExpansion } = useViewedCards(cardId);

    // Track when card is flipped to detail view
    useEffect(() => {
        trackExpansion(isFlipped);
    }, [isFlipped, trackExpansion]);

    // Default cover styling (for backward compatibility)
    const defaultCoverStyling = {
        titleAlign: 'center',
        titleSize: 'medium',
        titleColor: 'white',
        locationAlign: 'left',
        locationSize: 'small',
        locationColor: 'white',
        dateAlign: 'left',
        dateSize: 'small',
        dateColor: 'white',
        verticalAlign: 'bottom',
    };
    const styling = { ...defaultCoverStyling, ...coverStyling };

    // Default cover sections if none provided (for backward compatibility)
    const defaultCoverSections = coverSections.length > 0 ? coverSections : [
        {
            id: 'default-title',
            type: 'title',
            field: 'title',
            content: '',
            enabled: true,
            styling: {
                align: styling.titleAlign,
                verticalAlign: styling.verticalAlign,
                size: styling.titleSize,
                color: styling.titleColor,
                fontWeight: 'bold',
                textShadow: true,
                opacity: 1,
            },
            order: 0,
        },
        {
            id: 'default-location',
            type: 'location',
            field: 'locationTag',
            content: '',
            enabled: true,
            styling: {
                align: styling.locationAlign,
                verticalAlign: 'top',
                size: styling.locationSize,
                color: styling.locationColor,
                backgroundColor: 'rgba(255,255,255,0.25)',
                padding: '4px 10px',
                borderRadius: 10,
                opacity: 1,
            },
            order: 1,
        },
        {
            id: 'default-dates',
            type: 'dateRange',
            field: 'dates',
            content: '',
            enabled: true,
            styling: {
                align: styling.dateAlign,
                verticalAlign: styling.verticalAlign,
                size: styling.dateSize,
                color: styling.dateColor,
                fontWeight: '600',
                textShadow: true,
                opacity: 1,
            },
            order: 2,
        },
    ];

    const handleCardClick = (e) => {
        if (!showCoverOnly && !showDetailOnly) {
            setIsFlipped(!isFlipped);
        }
    };

    const handleGalleryImageClick = (e, imageUrl) => {
        e.stopPropagation();
        setSelectedGalleryImage(imageUrl);
    };

    const closeGalleryPreview = (e) => {
        e.stopPropagation();
        setSelectedGalleryImage(null);
    };

    const dateRange = startDate && endDate
        ? `${formatDateForPreview(startDate)} - ${formatDateForPreview(endDate)}`
        : '';

    // Combine cover image with gallery for display
    const allImages = [imageUrl, ...galleryImageUrls].filter(Boolean);

    // --- Base Styles ---
    const cardWrapperStyle = {
        height: '400px',
        width: '350px',  // Fixed width for consistent size
        perspective: '1000px',
        cursor: showCoverOnly || showDetailOnly ? 'default' : 'pointer',
        position: 'relative',
        backgroundColor: '#fff',  // White background for the card
        borderRadius: '8px',  // Match the card face radius
        margin: '20px',  // Add margin to prevent clipping
    };

    const cardFlipperStyle = {
        width: '100%',
        height: '100%',
        transition: 'transform 0.7s cubic-bezier(0.4, 0.0, 0.2, 1)',
        transformStyle: 'preserve-3d',
        position: 'relative',
        transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
    };

    const cardFaceBaseStyle = {
        position: 'absolute',
        width: '100%',
        height: '100%',
        backfaceVisibility: 'hidden',
        borderRadius: '8px',  // Match mobile's borderRadius: 8
        overflow: 'visible',  // Changed to visible to prevent clipping
        // Removed box shadow - mobile doesn't have shadow in preview
    };

    // --- Front Face Styles ---
    const cardFrontStyle = {
        ...cardFaceBaseStyle,
        backgroundColor: '#CCC',
    };

    // Build background overlay styles
    const getOverlayStyle = () => {
        const baseStyle = {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
        };

        if (!coverBackgroundStyle || coverBackgroundStyle.overlay === 'none') {
            return null;
        }

        if (coverBackgroundStyle.overlay === 'solid') {
            return {
                ...baseStyle,
                backgroundColor: coverBackgroundStyle.overlayColor || 'rgba(0,0,0,0.3)',
            };
        }

        return null;
    };

    const imageBackgroundStyle = {
        width: '100%',
        height: '100%',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundImage: `url(${imageUrl || 'https://via.placeholder.com/300x200/CCCCCC/FFFFFF?text=No+Image+Preview'})`,
        position: 'relative',
        borderRadius: '8px',  // Add border radius to image background
        overflow: 'hidden',  // Keep overflow hidden only for the background image
    };

    const overlayContentStyle = rnToWebStyle({
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        padding: 12,  // Match mobile's padding exactly (was 15)
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        color: '#FFFFFF',
        zIndex: 1,
        overflow: 'visible',  // Changed to visible to prevent content clipping
        borderRadius: '8px',  // Add border radius to maintain rounded corners
    });

    // Helper function to render a cover section
    const renderCoverSection = (section) => {
        if (!section.enabled) return null;

        const sectionStyle = {
            opacity: section.styling.opacity || 1,
        };

        // Get content based on field mapping
        let content = section.content;
        if (section.field === 'title') content = title;
        if (section.field === 'locationTag') content = locationTag;
        if (section.field === 'dates') content = dateRange;

        if (!content && section.field) return null;

        // Build text style
        const appliedFont = section.styling.font && section.styling.font !== 'system' ? section.styling.font : undefined;
        const fontSize = getTextSize(section.styling.size, section.type);
        const textStyle = {
            fontSize: fontSize,
            fontFamily: getWebFont(appliedFont || 'system'),
            fontWeight: (appliedFont && (appliedFont.includes('Bold') || appliedFont.includes('_Md'))) ? 'bold' : (section.styling.fontWeight || 'normal'),
            color: section.styling.color || 'white',
            textShadow: section.styling.textShadow 
                ? '1px 1px 2px rgba(0, 0, 0, 0.5)' 
                : 'none',
            textAlign: section.styling.align || 'left',
            lineHeight: 1.2,
            margin: 0,
            padding: 0,
            marginBottom: section.styling.marginBottom || 0,
        };

        // Handle different section types
        switch (section.type) {
            case 'title':
                // Always use Barlow for titles
                const titleTextStyle = {
                    ...textStyle,
                    fontFamily: "'Barlow', sans-serif",
                    fontWeight: 'bold',
                };
                return (
                    <div key={section.id} style={sectionStyle}>
                        <div style={titleTextStyle}>{content}</div>
                    </div>
                );
            
            case 'customText':
                return (
                    <div key={section.id} style={{
                        ...sectionStyle,
                        display: 'inline-block',
                        backgroundColor: section.styling.backgroundColor || 'transparent',
                        ...rnToWebStyle({
                            paddingHorizontal: section.styling.backgroundColor ? 10 : 0,
                            paddingVertical: section.styling.backgroundColor ? 4 : 0,
                        }),
                        borderRadius: section.styling.borderRadius !== undefined ? section.styling.borderRadius : (section.styling.backgroundColor ? 10 : 0),
                    }}>
                        <div style={textStyle}>{content}</div>
                    </div>
                );

            case 'location':
            case 'badge':
                const badgeStyle = {
                    display: 'inline-block',
                    backgroundColor: section.styling.backgroundColor || 'transparent',
                    ...rnToWebStyle({
                        paddingHorizontal: section.styling.backgroundColor ? 10 : 0,
                        paddingVertical: section.styling.backgroundColor ? 4 : 0,
                    }),
                    borderRadius: section.styling.borderRadius !== undefined ? section.styling.borderRadius : (section.styling.backgroundColor ? 10 : 0),
                    ...textStyle,
                };
                
                const wrapperStyle = {
                    textAlign: section.styling.align || 'left',
                    ...sectionStyle,
                };

                return (
                    <div key={section.id} style={wrapperStyle}>
                        <div style={badgeStyle}>
                            {section.type === 'location' && ''}
                            {content}
                        </div>
                    </div>
                );

            case 'dateRange':
                const dateContainerStyle = {
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: section.styling.backgroundColor || 'transparent',
                    ...rnToWebStyle({
                        paddingHorizontal: section.styling.backgroundColor ? 10 : 0,
                        paddingVertical: section.styling.backgroundColor ? 4 : 0,
                    }),
                    borderRadius: section.styling.borderRadius !== undefined ? section.styling.borderRadius : (section.styling.backgroundColor ? 10 : 0),
                };
                
                const dateWrapperStyle = {
                    display: 'flex',
                    justifyContent: section.styling.align === 'center' ? 'center' : 
                                   section.styling.align === 'right' ? 'flex-end' : 'flex-start',
                    ...sectionStyle,
                };

                return (
                    <div key={section.id} style={dateWrapperStyle}>
                        <div style={dateContainerStyle}>
                            <span style={textStyle}>{content}</span>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // Group sections by vertical alignment
    const groupSectionsByVerticalAlign = (sections) => {
        const groups = {
            top: [],
            middle: [],
            bottom: [],
        };

        sections
            .filter(s => s.enabled)
            .sort((a, b) => a.order - b.order)
            .forEach(section => {
                const align = section.styling.verticalAlign || 'bottom';
                groups[align].push(section);
            });

        return groups;
    };

    const sectionsToRender = coverSections.length > 0 ? coverSections : defaultCoverSections;
    const groupedSections = groupSectionsByVerticalAlign(sectionsToRender);

    // --- Back Face Styles ---
    const cardBackStyle = {
        ...cardFaceBaseStyle,
        backgroundColor: detailBackgroundColor,
        color: '#333333',
        transform: 'rotateY(180deg)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    };

    const backFlipButtonStyle = rnToWebStyle({
        background: 'none',
        border: 'none',
        color: '#666',
        cursor: 'pointer',
        fontSize: 14,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
        transition: 'background-color 0.2s',
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 10,
    });

    const scrollableContentStyle = rnToWebStyle({
        flex: 1,
        overflowY: 'auto',
        overflowX: 'visible',  // Changed to visible to prevent horizontal clipping
        paddingTop: 30,
        paddingBottom: 20,
        paddingHorizontal: 20,
        WebkitOverflowScrolling: 'touch', // For smooth scrolling on iOS
        scrollbarWidth: 'thin', // For Firefox
        scrollbarColor: '#ccc #f5f5f5', // For Firefox
    });

    const backContentSectionStyle = {
        marginBottom: '16px',
        // Allow content to be visible
        maxWidth: '100%',
        overflow: 'visible',
    };

    const backLabelStyle = {
        fontWeight: '600',
        fontSize: '14px',
        color: '#555',
        marginBottom: '6px',
        display: 'block',
    };

    const backDescriptionStyle = {
        fontSize: '14px',
        lineHeight: '1.6',
        whiteSpace: 'pre-wrap',
        color: '#666',
        // Text wrapping to prevent overflow
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
    };

    const galleryContainerStyle = {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        marginTop: '8px',
    };

    const galleryThumbnailStyle = {
        width: '80px',
        height: '80px',
        objectFit: 'cover',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'transform 0.2s',
    };

    const mapPlaceholderStyle = {
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        padding: '20px',
        textAlign: 'center',
        color: '#999',
        fontSize: '13px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
    };

    const destinationTagStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: '#f0f8ff',
        color: '#007AFF',
        padding: '6px 12px',
        borderRadius: '8px',
        fontSize: '14px',
        gap: '5px',
    };

    const emptyStateStyle = rnToWebStyle({
        textAlign: 'center',
        color: '#999',
        fontStyle: 'italic',
        fontSize: 13,
        padding: 10,
    });

    const flipHintStyle = rnToWebStyle({
        backgroundColor: 'rgba(255,255,255,0.25)',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 10,
        fontSize: 12,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        position: 'absolute',
        top: 15,
        right: 15,
    });

    // Gallery preview modal styles
    const galleryModalStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        cursor: 'pointer',
    };

    const galleryModalImageStyle = {
        maxWidth: '90%',
        maxHeight: '90%',
        objectFit: 'contain',
        borderRadius: '8px',
    };

    const galleryCloseButtonStyle = {
        position: 'absolute',
        top: '20px',
        right: '20px',
        color: 'white',
        fontSize: '30px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
    };

    // Helper function to render detail sections
    const renderDetailSection = (section, index) => {
        const getDetailAlignment = (align) => {
            return align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
        };
        
        // Apply margins and padding from section styling
        // Use platform-specific padding if available
        const getPlatformPadding = (direction) => {
            const platformKey = previewPlatform === 'ios' ? `ios${direction}` : 
                               previewPlatform === 'android' ? `android${direction}` : null;
            
            // Return platform-specific padding if defined, otherwise fall back to default
            if (platformKey && section.styling[platformKey] !== undefined) {
                return section.styling[platformKey];
            }
            return section.styling[`padding${direction}`] || 0;
        };
        
        const sectionContainerStyle = rnToWebStyle({
            marginTop: section.styling.marginTop || 0,
            marginBottom: section.styling.marginBottom || 20,
            marginLeft: section.styling.marginLeft || 0,
            marginRight: section.styling.marginRight || 0,
            paddingTop: getPlatformPadding('PaddingTop'),
            paddingBottom: getPlatformPadding('PaddingBottom'),
            paddingLeft: getPlatformPadding('PaddingLeft'),
            paddingRight: getPlatformPadding('PaddingRight'),
            textAlign: section.styling.align,
            display: 'flex',
            flexDirection: 'column',
            alignItems: getDetailAlignment(section.styling.align),
            // Allow content to be visible
            maxWidth: '100%',
            overflow: 'visible',
            boxSizing: 'border-box',
        });

        // Common text style for preventing overflow
        const textOverflowStyle = {
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
        };

        switch (section.type) {
            case 'title':
                const titleFontSize = getTextSize(section.styling.size, 'detailTitle');
                return (
                    <div key={index} style={sectionContainerStyle}>
                        <div style={{
                            fontSize: titleFontSize,
                            fontFamily: getWebFont(section.styling.font || 'system'),
                            color: section.styling.color || 'black',
                            margin: 0,
                            padding: 0,
                            fontWeight: (section.styling.font && (section.styling.font.includes('Bold') || section.styling.font.includes('_Md'))) ? 'bold' : (section.styling.fontWeight || 'bold'),
                            textAlign: section.styling.align || 'left',
                            ...textOverflowStyle,
                        }}>
                            {section.content || 'Untitled Section'}
                        </div>
                    </div>
                );
            case 'text':
                const textFontSize = typeof section.styling.size === 'number' ? section.styling.size : parseFloat(getTextSize(section.styling.size, 'detailText'));
                return (
                    <div key={index} style={sectionContainerStyle}>
                        <div style={{
                            fontSize: `${textFontSize}px`,
                            fontFamily: getWebFont(section.styling.font || 'system'),
                            color: section.styling.color || 'black',
                            lineHeight: textFontSize * 1.6 / textFontSize, // Matches mobile's fontSize * 1.6
                            whiteSpace: 'pre-wrap',
                            margin: 0,
                            padding: 0,
                            ...textOverflowStyle,
                        }}>
                            {section.content || 'No content'}
                        </div>
                    </div>
                );
            case 'description':
                return (
                    <div key={index} style={sectionContainerStyle}>
                        <div style={backContentSectionStyle}>
                            <span style={backLabelStyle}>About this trip:</span>
                            {section.content ? (
                                <p style={{
                                    ...backDescriptionStyle,
                                    fontSize: getTextSize(section.styling.size, 'detailText'),
                                    color: section.styling.color,
                                    margin: 0,
                                    ...textOverflowStyle,
                                }}>
                                    {section.content}
                                </p>
                            ) : (
                                <div style={emptyStateStyle}>No description added yet</div>
                            )}
                        </div>
                    </div>
                );

            case 'image':
                const imageUrl = detailImagePreviews[section.id] || section.content;
                return imageUrl ? (
                    <div key={index} style={sectionContainerStyle}>
                        <img
                            src={imageUrl}
                            alt="Detail"
                            style={{
                                maxWidth: '100%',
                                borderRadius: '8px',
                                height: 'auto',
                            }}
                        />
                    </div>
                ) : null;

            case 'carousel':
                const carouselImages = section.content || [];
                return carouselImages.length > 0 ? (
                    <div key={index} style={sectionContainerStyle}>
                        <div className="gallery-scroll" style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
                            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', paddingBottom: '10px' }}>
                                {carouselImages.map((img, idx) => (
                                    <img
                                        key={idx}
                                        src={img}
                                        alt={`Carousel ${idx + 1}`}
                                        style={{
                                            height: '150px',
                                            width: 'auto',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                        }}
                                        onClick={(e) => handleGalleryImageClick(e, img)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                ) : null;

            case 'backgroundImage':
                const bgImageUrl = detailImagePreviews[section.id] || section.content;
                return bgImageUrl ? (
                    <div
                        key={index}
                        style={{
                            ...sectionContainerStyle,
                            backgroundImage: `url(${bgImageUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            minHeight: '200px',
                            borderRadius: '8px',
                            // Adjusted margins to prevent overflow
                            marginLeft: Math.max(-20, section.styling.marginLeft || 0),
                            marginRight: Math.max(-20, section.styling.marginRight || 0),
                            padding: '20px',
                            position: 'relative',
                            width: `calc(100% + ${40 - Math.max(0, section.styling.marginLeft || 0) - Math.max(0, section.styling.marginRight || 0)}px)`,
                            maxWidth: `calc(100% + 40px)`,
                        }}
                    >
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            borderRadius: '8px',
                        }} />
                    </div>
                ) : null;

            case 'link':
                return section.content ? (
                    <div key={index} style={sectionContainerStyle}>
                        <a href={section.content}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: '#007AFF',
                                fontSize: getTextSize(section.styling.size, 'detailText'),
                                fontFamily: getWebFont(section.styling.font || 'system'),
                                textDecoration: 'underline',
                                ...textOverflowStyle,
                            }}
                        >
                            {section.content}
                        </a>
                    </div>
                ) : null;

            case 'map':
                return section.content && (section.content.latitude || section.content.longitude) ? (
                    <div key={index} style={sectionContainerStyle}>
                        <div style={mapPlaceholderStyle}>
                            <span style={{ fontSize: '24px' }}>🗺️</span>
                            {section.content.latitude && section.content.longitude ? (
                                <span>{parseFloat(section.content.latitude).toFixed(4)}, {parseFloat(section.content.longitude).toFixed(4)}</span>
                            ) : (
                                <span>Incomplete coordinates</span>
                            )}
                        </div>
                    </div>
                ) : null;

            default:
                return null;
        }
    };

    // Custom scrollbar CSS and React Native-like defaults for the preview
    const scrollbarStyles = `
        .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
            background: #f5f5f5;
            border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 4px;
        }
        
        /* React Native-like box model defaults */
        .rn-preview * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
        }
        
        .rn-preview div {
            display: flex;
            flex-direction: column;
            flex-shrink: 0;
            position: relative;
        }
        
        .rn-preview p, .rn-preview h1, .rn-preview h2, .rn-preview h3, .rn-preview h4, .rn-preview h5, .rn-preview h6 {
            margin: 0;
            padding: 0;
        }
        
        /* Override for specific inline elements */
        .rn-preview span,
        .rn-preview img {
            display: inline;
            flex-direction: initial;
        }
        
        /* Momentum scrolling for galleries */
        .gallery-scroll {
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
        }
        
        /* Hide scrollbars on galleries to match mobile */
        .gallery-scroll::-webkit-scrollbar {
            display: none;
        }
        .gallery-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
    `;

    return (
        <>
            <style>{scrollbarStyles}</style>
            <div className="rn-preview" style={{...cardWrapperStyle, position: 'relative'}} onClick={handleCardClick} title={showCoverOnly || showDetailOnly ? undefined : "Click to flip card"}>
                {isViewed && (
                    <>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(26, 32, 46, 0.35)', // Dark blue from background
                            borderRadius: '8px',
                            zIndex: 10,
                            pointerEvents: 'none'
                        }} />
                        <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            zIndex: 11,
                            pointerEvents: 'none'
                        }}>
                            <span style={{ fontSize: '18px', color: '#FFD700' }}>✓</span>
                        </div>
                    </>
                )}
                {showDetailOnly ? (
                    // Render detail view directly without flip animation
                    <div style={{ width: '100%', height: '100%' }}>
                        <div style={{
                            ...cardFaceBaseStyle,
                            backgroundColor: detailBackgroundColor,
                            color: '#333333',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                        }}>
                            {/* Scrollable content container */}
                            <div 
                                className="custom-scrollbar"
                                style={scrollableContentStyle}
                            >
                                {/* Render custom detail sections in order */}
                                {detailSections.length > 0 ? (
                                    detailSections
                                        .sort((a, b) => a.order - b.order)
                                        .map((section, index) => renderDetailSection(section, index))
                                ) : (
                                    <>
                                        {/* Default sections if no custom sections */}
                                        {showGallery && (
                                            <div style={backContentSectionStyle}>
                                                <span style={backLabelStyle}>Photos ({allImages.length}):</span>
                                                {allImages.length > 0 ? (
                                                    <div style={galleryContainerStyle}>
                                                        {allImages.map((img, index) => (
                                                            <img
                                                                key={index}
                                                                src={img}
                                                                alt={`Gallery ${index + 1}`}
                                                                style={galleryThumbnailStyle}
                                                                onClick={(e) => handleGalleryImageClick(e, img)}
                                                                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                                                                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                                            />
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div style={emptyStateStyle}>No gallery images added</div>
                                                )}
                                            </div>
                                        )}

                                        {locationTag && (
                                            <div style={backContentSectionStyle}>
                                                <span style={backLabelStyle}>Destination:</span>
                                                <div style={destinationTagStyle}>
                                                    <span></span>
                                                    <span>{locationTag}</span>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    // Regular flip card behavior
                    <div style={cardFlipperStyle}>
                        {/* Front of the Card */}
                        <div style={cardFrontStyle}>
                            <div style={imageBackgroundStyle}>
                                {/* Background Overlay */}
                                {getOverlayStyle() && <div style={getOverlayStyle()} />}
                                
                                {/* Content */}
                                <div style={overlayContentStyle}>
                                    {/* Top-aligned sections */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {groupedSections.top.map(renderCoverSection)}
                                    </div>

                                    {/* Middle-aligned sections */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '8px' }}>
                                        {groupedSections.middle.map(renderCoverSection)}
                                    </div>

                                    {/* Bottom-aligned sections */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {groupedSections.bottom.map(renderCoverSection)}
                                    </div>

                                    {/* Flip hint */}
                                    {!showCoverOnly && (
                                        <div style={flipHintStyle}>
                                            <span>↻</span>
                                            <span>Tap to flip</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Back of the Card */}
                        <div style={cardBackStyle}>
                            {!showDetailOnly && (
                                <button 
                                    style={backFlipButtonStyle}
                                    onMouseOver={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                                    onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                                >
                                    ← Back
                                </button>
                            )}

                            {/* Scrollable content container */}
                            <div 
                                className="custom-scrollbar"
                                style={scrollableContentStyle}
                            >
                                {/* Render custom detail sections in order */}
                                {detailSections.length > 0 ? (
                                    detailSections
                                        .sort((a, b) => a.order - b.order)
                                        .map((section, index) => renderDetailSection(section, index))
                                ) : (
                                    <>
                                        {/* Default sections if no custom sections */}
                                        {showGallery && (
                                            <div style={backContentSectionStyle}>
                                                <span style={backLabelStyle}>Photos ({allImages.length}):</span>
                                                {allImages.length > 0 ? (
                                                    <div style={galleryContainerStyle}>
                                                        {allImages.map((img, index) => (
                                                            <img
                                                                key={index}
                                                                src={img}
                                                                alt={`Gallery ${index + 1}`}
                                                                style={galleryThumbnailStyle}
                                                                onClick={(e) => handleGalleryImageClick(e, img)}
                                                                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                                                                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                                            />
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div style={emptyStateStyle}>No gallery images added</div>
                                                )}
                                            </div>
                                        )}

                                        {locationTag && (
                                            <div style={backContentSectionStyle}>
                                                <span style={backLabelStyle}>Destination:</span>
                                                <div style={destinationTagStyle}>
                                                    <span></span>
                                                    <span>{locationTag}</span>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Gallery Preview Modal */}
            {selectedGalleryImage && (
                <div style={galleryModalStyle} onClick={closeGalleryPreview}>
                    <button style={galleryCloseButtonStyle}>×</button>
                    <img 
                        src={selectedGalleryImage} 
                        alt="Gallery preview" 
                        style={galleryModalImageStyle}
                    />
                </div>
            )}
        </>
    );
}

export default WebExploreCardPreview;